import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CheckCircle2, Loader2 } from "lucide-react";
import { TackLogo } from "@/components/TackLogo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";

const searchSchema = z.object({
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/checkout/return")({
  component: CheckoutReturn,
  validateSearch: (search) => searchSchema.parse(search),
});

/**
 * Landing page after Stripe embedded checkout completes (or a redirect-based
 * payment method sends the customer back). Fulfilment itself happens via the
 * Stripe webhook, so this page just reassures the customer and routes them
 * onward — it never polls Stripe directly.
 */
function CheckoutReturn() {
  const { t } = useTranslation();
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate({ to: "/dashboard" });
    }, 4000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <TackLogo size="lg" />
          </div>
          <Card>
            <CardHeader className="text-center py-10">
              {session_id ? (
                <>
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="font-display">{t("checkoutReturn.successTitle")}</CardTitle>
                  <CardDescription>{t("checkoutReturn.successDesc")}</CardDescription>
                </>
              ) : (
                <>
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                  <CardTitle className="font-display mt-4 text-lg">
                    {t("checkoutReturn.pendingTitle")}
                  </CardTitle>
                </>
              )}
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <Link to="/dashboard">{t("checkoutReturn.goToDashboard")}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <SiteFooter variant="legal" />
    </div>
  );
}
